<script setup lang="ts">
import { useRouter } from "vue-router";
import { ref, shallowRef, watch } from "vue";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { useVoiceMachineStore } from "@/stores/voice-machine-store";

import VoiceTranscriptUtterances from "./VoiceTranscriptUtterances.vue";

import type { Utterance } from "@/stores/case-file-store";

const props = defineProps<{ utterances: Utterance[]; fileReference: string }>();

const $router = useRouter();
const routeParams = useCaseRouteParams();

const searchText = ref("");
const searchResults = shallowRef<Utterance[]>([]);

function goToSearchResult(mark: Record<"startTime", number>) {
  const time = +mark.startTime.toFixed(2);
  const { brand, casename } = routeParams.value;

  $router.push({
    name: "casefiletime",
    params: { brand, casename, casefile: props.fileReference, time },
    force: true,
  });

  useVoiceMachineStore().setTimeRange("transcript", props.fileReference, time);
}

function searchTranscript(value: string) {
  const contextWordsPadding = 5;
  const text = value.trim();

  if (text.length < 2) {
    searchResults.value = [];
    return;
  }

  const regex = new RegExp(
    `(?:[\\w',.]+?\\s){0,${contextWordsPadding}}(${text})(?:.*?\\s){0,${contextWordsPadding}}`,
    "i",
  );

  searchResults.value = props.utterances.reduce<typeof searchResults.value>(
    (acc, { dialogueText, endTime, isContext, speaker, startTime }) => {
      if (startTime == null || endTime == null) return acc;

      const match = dialogueText.match(regex);
      if (match?.index == null || match.input == null) return acc;

      const words = match[0].replace(match[1], `<span class='tw-text-red-700'>${match[1]}</span>`).trim();

      return [
        ...acc,
        {
          startTime,
          endTime,
          isContext,
          speaker,
          dialogueText: `${match.index === 0 ? "" : "..."}${words}${
            match.index + match[0].length === match.input.length ? "" : "..."
          }`,
        },
      ];
    },
    [] as typeof searchResults.value,
  );
}

watch(
  () => props.fileReference,
  () => {
    searchText.value = "";
    searchResults.value = [];
  },
);

watch(searchText, searchTranscript);
</script>
<template>
  <div class="tw-flex tw-h-full tw-w-1/4 tw-flex-col tw-items-start tw-justify-start tw-gap-1 tw-divide-y tw-shadow">
    <div class="tw-w-full tw-p-2">
      <input
        v-model="searchText"
        type="text"
        placeholder="Enter search query"
        class="tw-w-full tw-rounded-md tw-bg-white tw-p-1"
      />
    </div>
    <div
      v-if="searchResults.length < 1"
      class="tw-grid tw-w-full tw-grow tw-place-items-center tw-text-pretty tw-text-center"
    >
      Enter characters to start search
    </div>
    <div v-else class="tw-w-full tw-grow tw-overflow-y-auto tw-p-1">
      <div
        v-for="result in searchResults"
        :key="result.startTime"
        class="tw-cursor-pointer tw-select-none tw-rounded-md tw-px-2 tw-py-1 hover:tw-bg-white"
        @click="() => goToSearchResult(result)"
      >
        <VoiceTranscriptUtterances :file-reference="fileReference" :list="[result]" />
      </div>
    </div>
  </div>
</template>
